import { useState, useContext } from "react";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import ThemeContext from "../lib/theme-context";
import CustomDatePicker from "../components/dateTimePickers/datePicker";
import CustomDateTimePicker from "../components/dateTimePickers/DateTimePicker";
import dayjs from "dayjs";

const AdvancedFilterControls = ({ 
  filter, 
  onUpdateFilter,
  fieldInfo 
}) => {
  const theme = useContext(ThemeContext);
  
  // P/L Range state
  const [plRange, setPlRange] = useState([0, 1000]);
  const [plRangeType, setPlRangeType] = useState("dollar"); // "dollar" or "percentage"
  
  // Date Range state
  const [dateRange, setDateRange] = useState({
    start: "",
    end: ""
  });

  const handlePlRangeChange = (event, newValue) => {
    setPlRange(newValue);
    // Update filter with range format including type prefix
    const prefix = plRangeType === "percentage" ? "pct:" : "$:";
    onUpdateFilter(filter.id, "value", `${prefix}${newValue[0]},${newValue[1]}`);
  };

  const handleDateRangeChange = (type, value) => {
    // Convert Date object to string format for storage
    const dateString = value ? new Date(value).toISOString().slice(0, 19).replace('T', ' ') : '';
    const newDateRange = { ...dateRange, [type]: dateString };
    setDateRange(newDateRange);
    
    // Update filter with date range format
    if (newDateRange.start && newDateRange.end) {
      onUpdateFilter(filter.id, "value", `${newDateRange.start},${newDateRange.end}`);
    } else if (newDateRange.start) {
      onUpdateFilter(filter.id, "value", newDateRange.start);
    } else {
      onUpdateFilter(filter.id, "value", "");
    }
  };

  // Render different controls based on field type
  if (fieldInfo?.type === "number" && fieldInfo?.label?.includes("P/L")) {
    return (
      <Box sx={{ 
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexWrap: "nowrap",
      }}>
        {/* P/L Range Type Toggle */}
        <Box sx={{ 
          display: "flex", 
          flexShrink: 0,
          border: `1px solid ${theme[0] === "dark" ? "#4a5568" : "#d1d5db"}`,
          borderRadius: "6px",
          overflow: "hidden",
        }}>
          <Box
            onClick={() => {
              setPlRangeType("dollar");
              onUpdateFilter(filter.id, "value", `$:${plRange[0]},${plRange[1]}`);
            }}
            sx={{
              padding: "4px 10px",
              fontSize: "12px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              backgroundColor: plRangeType === "dollar" 
                ? theme[0] === "dark" ? "#1C83DE" : "#2563eb"
                : "transparent",
              color: plRangeType === "dollar" 
                ? "#ffffff"
                : theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              borderRight: `1px solid ${theme[0] === "dark" ? "#4a5568" : "#d1d5db"}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: plRangeType === "dollar" 
                  ? theme[0] === "dark" ? "#1C83DE" : "#2563eb"
                  : theme[0] === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
              },
            }}
          >
            $
          </Box>
          <Box
            onClick={() => {
              setPlRangeType("percentage");
              onUpdateFilter(filter.id, "value", `pct:${plRange[0]},${plRange[1]}`);
            }}
            sx={{
              padding: "4px 10px",
              fontSize: "12px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              backgroundColor: plRangeType === "percentage" 
                ? theme[0] === "dark" ? "#1C83DE" : "#2563eb"
                : "transparent",
              color: plRangeType === "percentage" 
                ? "#ffffff"
                : theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: plRangeType === "percentage" 
                  ? theme[0] === "dark" ? "#1C83DE" : "#2563eb"
                  : theme[0] === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
              },
            }}
          >
            %
          </Box>
        </Box>

        {/* Slider + Text stacked */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px", minWidth: 0, padding: "0 12px 0 20px" }}>
          <Slider
            value={plRange}
            onChange={handlePlRangeChange}
            valueLabelDisplay="off"
            min={plRangeType === "dollar" ? -500 : -50}
            max={plRangeType === "dollar" ? 500 : 50}
            step={plRangeType === "dollar" ? 25 : 1}
            size="small"
            sx={{
              width: "100%",
              padding: "4px 0 0",
              "& .MuiSlider-thumb": {
                backgroundColor: theme[0] === "dark" ? "#1C83DE" : "#2563eb",
                width: 18,
                height: 18,
                boxShadow: "none",
                border: `2px solid ${theme[0] === "dark" ? "#1e1f23" : "#ffffff"}`,
                "&:hover, &.Mui-focusVisible": {
                  boxShadow: `0 0 0 6px ${theme[0] === "dark" ? "rgba(28, 131, 222, 0.2)" : "rgba(37, 99, 235, 0.2)"}`,
                },
              },
              "& .MuiSlider-track": {
                backgroundColor: theme[0] === "dark" ? "#1C83DE" : "#2563eb",
                height: 5,
                border: "none",
              },
              "& .MuiSlider-rail": {
                backgroundColor: theme[0] === "dark" ? "#4a5568" : "#d1d5db",
                height: 5,
                opacity: 0.5,
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontSize: "10px",
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              whiteSpace: "nowrap",
              textAlign: "center",
              lineHeight: 1,
            }}
          >
            {plRangeType === "dollar" 
              ? `$${plRange[0]} to $${plRange[1]}`
              : `${plRange[0]}% to ${plRange[1]}%`
            }
          </Typography>
        </Box>
      </Box>
    );
  }

  if (fieldInfo?.type === "date") {
    // Always use CustomDateTimePicker for Entry and Exit fields since they need date and time
    const isDateTimeField = fieldInfo.label?.includes("Entry") || fieldInfo.label?.includes("Exit");
    const DatePickerComponent = isDateTimeField ? CustomDateTimePicker : CustomDatePicker;
    
    // For single date operations, extract the single date from the filter value
    const getSingleDateValue = () => {
      if (filter.value && !filter.value.includes(",")) {
        return filter.value;
      }
      return "";
    };
    
    const handleSingleDateChange = (value) => {
      // Convert Date object to string format for storage
      const dateString = value ? new Date(value).toISOString().slice(0, 19).replace('T', ' ') : '';
      onUpdateFilter(filter.id, "value", dateString);
    };
    
    return (
      <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        {filter.operator === "between" ? (
          <Box sx={{ 
            display: "flex", 
            gap: "4px", 
            alignItems: "center",
            flexWrap: "nowrap",
          }}>
            <DatePickerComponent
              label=""
              value={dateRange.start ? dayjs(dateRange.start) : null}
              onChange={(newValue) => handleDateRangeChange("start", newValue ? newValue.$d : null)}
              size="small"
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: "10px",
                color: theme[0] === "dark" ? "#6b7280" : "#9ca3af",
                whiteSpace: "nowrap",
              }}
            >
              to
            </Typography>
            <DatePickerComponent
              label=""
              value={dateRange.end ? dayjs(dateRange.end) : null}
              onChange={(newValue) => handleDateRangeChange("end", newValue ? newValue.$d : null)}
              size="small"
            />
          </Box>
        ) : (
          // Single date picker for other operators
          <DatePickerComponent
            label=""
            value={getSingleDateValue() ? dayjs(getSingleDateValue()) : null}
            onChange={(newValue) => handleSingleDateChange(newValue ? newValue.$d : null)}
            size="small"
          />
        )}
      </Box>
    );
  }

  return null;
};

export default AdvancedFilterControls;
